export const BASE_SITE_URL = 'https://www.babete.pt';

export const DEFAULT_PUBLIC_LOCALE = 'pt';
export const PUBLIC_LOCALES = ['pt', 'en', 'fr', 'es'];

export const PUBLIC_INDEXABLE_ROUTE_DEFINITIONS = [
  {
    id: 'homepage',
    basePath: '/',
    titleKey: 'seo.title',
    descriptionKey: 'seo.description',
    changefreq: 'weekly',
    priority: '1.0',
  },
  {
    id: 'manifesto',
    basePath: '/manifesto',
    titleKey: 'manifesto.seo_title',
    descriptionKey: 'manifesto.seo_description',
    changefreq: 'monthly',
    priority: '0.8',
  },
  {
    id: 'pledge',
    basePath: '/pledge',
    titleKey: 'pledge.seo_title',
    descriptionKey: 'pledge.seo_description',
    changefreq: 'monthly',
    priority: '0.8',
  },
  {
    id: 'privacy',
    basePath: '/privacy',
    titleKey: 'privacy.seo_title',
    descriptionKey: 'privacy.seo_description',
    changefreq: 'yearly',
    priority: '0.5',
  },
  {
    id: 'terms',
    basePath: '/terms',
    titleKey: 'terms.seo_title',
    descriptionKey: 'terms.seo_description',
    changefreq: 'yearly',
    priority: '0.5',
  },
  {
    id: 'urbanismo',
    basePath: '/urbanismo',
    titleKey: 'landing.urbanismo.seo_title',
    descriptionKey: 'landing.urbanismo.seo_description',
    changefreq: 'weekly',
    priority: '0.9',
  },
  {
    id: 'condominios',
    basePath: '/condominios',
    titleKey: 'landing.condominios.seo_title',
    descriptionKey: 'landing.condominios.seo_description',
    changefreq: 'weekly',
    priority: '0.9',
  },
  {
    id: 'pmeToolkit',
    basePath: '/pme-toolkit',
    titleKey: 'landing.pmeToolkit.seo_title',
    descriptionKey: 'landing.pmeToolkit.seo_description',
    changefreq: 'weekly',
    priority: '0.9',
  },
];

const PUBLIC_INDEXABLE_BASE_PATHS = new Set(
  PUBLIC_INDEXABLE_ROUTE_DEFINITIONS.map((route) => route.basePath)
);

export const normalizePublicPathname = (pathname = '/') => {
  if (!pathname || pathname === '/') return '/';

  const trimmed = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return trimmed || '/';
};

export const isSupportedPublicLocale = (locale) => PUBLIC_LOCALES.includes(locale);

export const stripLocaleFromPathname = (pathname = '/') => {
  const normalizedPathname = normalizePublicPathname(pathname);

  if (normalizedPathname === '/') {
    return {
      locale: DEFAULT_PUBLIC_LOCALE,
      basePath: '/',
      hasLocalePrefix: false,
    };
  }

  const segments = normalizedPathname.split('/').filter(Boolean);
  const maybeLocale = segments[0]?.toLowerCase();

  if (isSupportedPublicLocale(maybeLocale) && maybeLocale !== DEFAULT_PUBLIC_LOCALE) {
    const remainder = segments.slice(1);

    return {
      locale: maybeLocale,
      basePath: remainder.length ? `/${remainder.join('/')}` : '/',
      hasLocalePrefix: true,
    };
  }

  return {
    locale: DEFAULT_PUBLIC_LOCALE,
    basePath: normalizedPathname,
    hasLocalePrefix: false,
  };
};

export const isIndexablePublicBasePath = (basePath) => {
  return PUBLIC_INDEXABLE_BASE_PATHS.has(normalizePublicPathname(basePath));
};

export const isIndexablePublicPathname = (pathname) => {
  const { basePath } = stripLocaleFromPathname(pathname);
  return isIndexablePublicBasePath(basePath);
};

export const buildLocalizedPublicPath = (basePath, locale = DEFAULT_PUBLIC_LOCALE) => {
  const normalizedBasePath = normalizePublicPathname(basePath);
  const normalizedLocale = isSupportedPublicLocale(locale) ? locale : DEFAULT_PUBLIC_LOCALE;

  if (normalizedLocale === DEFAULT_PUBLIC_LOCALE) {
    return normalizedBasePath;
  }

  if (normalizedBasePath === '/') {
    return `/${normalizedLocale}`;
  }

  return `/${normalizedLocale}${normalizedBasePath}`;
};

export const buildLocalizedPublicUrl = (basePath, locale = DEFAULT_PUBLIC_LOCALE) => {
  return `${BASE_SITE_URL}${buildLocalizedPublicPath(basePath, locale)}`;
};

export const buildPublicMarkdownPath = (basePath, locale = DEFAULT_PUBLIC_LOCALE) => {
  const normalizedBasePath = normalizePublicPathname(basePath);
  const normalizedLocale = isSupportedPublicLocale(locale) ? locale : DEFAULT_PUBLIC_LOCALE;
  const slug =
    normalizedBasePath === '/'
      ? 'index.md'
      : `${normalizedBasePath.replace(/^\/+/, '')}.md`;

  if (normalizedLocale === DEFAULT_PUBLIC_LOCALE) {
    return `/${slug}`;
  }

  return `/${normalizedLocale}/${slug}`;
};

export const buildPublicMarkdownUrl = (basePath, locale = DEFAULT_PUBLIC_LOCALE) => {
  return `${BASE_SITE_URL}${buildPublicMarkdownPath(basePath, locale)}`;
};

export const buildPublicHreflangMap = (basePath) => {
  const normalizedBasePath = normalizePublicPathname(basePath);
  const hrefMap = {};

  for (const locale of PUBLIC_LOCALES) {
    hrefMap[locale] = buildLocalizedPublicUrl(normalizedBasePath, locale);
  }

  hrefMap['x-default'] = buildLocalizedPublicUrl(normalizedBasePath, DEFAULT_PUBLIC_LOCALE);

  return hrefMap;
};

export const getPublicRouteDefinitionByBasePath = (basePath) => {
  const normalizedBasePath = normalizePublicPathname(basePath);
  return PUBLIC_INDEXABLE_ROUTE_DEFINITIONS.find((route) => route.basePath === normalizedBasePath) || null;
};

export const extractBasePathFromCanonical = (canonicalUrl) => {
  if (!canonicalUrl) return null;

  try {
    const parsed = new URL(canonicalUrl);
    if (parsed.origin !== BASE_SITE_URL) {
      return null;
    }

    return normalizePublicPathname(parsed.pathname || '/');
  } catch {
    return null;
  }
};

export const getLocalizedSwitchPath = (pathname, locale) => {
  const { basePath } = stripLocaleFromPathname(pathname);

  if (!isIndexablePublicBasePath(basePath)) {
    return normalizePublicPathname(pathname);
  }

  return buildLocalizedPublicPath(basePath, locale);
};

export const getLocalizedLinkForCurrentPath = (pathname, targetBasePath) => {
  const { locale } = stripLocaleFromPathname(pathname);
  return buildLocalizedPublicPath(targetBasePath, locale);
};

export const getAllLocalizedPublicPaths = () => {
  return PUBLIC_INDEXABLE_ROUTE_DEFINITIONS.flatMap((route) =>
    PUBLIC_LOCALES.map((locale) => buildLocalizedPublicPath(route.basePath, locale))
  );
};
